import React, { useState, useCallback, useEffect } from "react";
import { Card, CardBody, Nav, NavItem, NavLink, Button } from "reactstrap";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  Polyline,
  OverlayView,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import Location from "../../assets/images/svg/Location.svg";
import RouteIconUrl from "../../assets/images/svg/route.svg";
import FuelIconUrl from "../../assets/images/svg/fuel.svg";
import PlusIconUrl from "../../assets/images/svg/plus.svg";
import HumanIconUrl from "../../assets/images/svg/Human.svg";
import Flag from "../../assets/images/svg/flag-svgrepo-com.svg";
import "./style.scss";

export const RealTimeMap = ({
  data,
  mapRef,
  focusOnMarker,
  polylineData,
  focusedCoords,
  setTripId,
  tripId,
}) => {
  const [activeTab, setActiveTab] = useState("State");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [stationName, setStationName] = useState(null);
  const navigate = useNavigate();

  const handleOpenInNewTab = () => {
    navigate("/map-view");
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const mapContainerStyle = { width: "100%", height: "100%" };
  const defaultCenter =
    focusedCoords && !isNaN(focusedCoords.lat) && !isNaN(focusedCoords.lng)
      ? {
          lat: parseFloat(focusedCoords.lat),
          lng: parseFloat(focusedCoords.lng),
        }
      : data.length > 0
      ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lng) }
      : { lat: 45.4654, lng: 9.1859 };

  const handleMarkerClick = useCallback(
    (trip) => {
      const lat = parseFloat(trip.lat);
      const lng = parseFloat(trip.lng);

      if (isNaN(lat) || isNaN(lng)) {
        console.error("Invalid trip coordinates:", trip);
        return;
      }

      setSelectedTrip(trip);
      focusOnMarker(lat, lng);
      searchNearbyStations(lat, lng);
    },
    [focusOnMarker]
  );

  const searchNearbyStations = (lat, lng) => {
    if (!mapRef.current) return;
    const service = new window.google.maps.places.PlacesService(mapRef.current);

    service.nearbySearch(
      {
        location: new window.google.maps.LatLng(lat, lng),
        radius: 5000,
        type: "gas_station",
      },
      (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results.length > 0
        ) {
          setStationName(results[0].name);
        } else {
          setStationName(null);
        }
      }
    );
  };
  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <Card className="realTimeTripCard h-100">
      <CardBody className="d-flex flex-column h-100">
        <Button className="tripMapButton mb-2" onClick={handleOpenInNewTab}>
          Open in Full Map View
        </Button>

        <div className="map-wrapper" style={{ flex: 1 }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={
              selectedTrip
                ? {
                    lat: parseFloat(selectedTrip.lat),
                    lng: parseFloat(selectedTrip.lng),
                  }
                : defaultCenter
            }
            zoom={6}
            onLoad={(map) => (mapRef.current = map)}
          >
            {data.map((trip, idx) => {
              const lat = parseFloat(trip.lat);
              const lng = parseFloat(trip.lng);
              if (isNaN(lat) || isNaN(lng)) {
                console.warn(`Invalid lat/lng for trip at index ${idx}`, {
                  lat: trip.lat,
                  lng: trip.lng,
                });
                return null;
              }

              return (
                <Marker
                  key={idx}
                  position={{ lat, lng }}
                  icon={{
                    url: trip.icon_url,
                    scaledSize:
                      trip.id === tripId
                        ? new window.google.maps.Size(48, 48)
                        : new window.google.maps.Size(34, 34),
                    anchor: new window.google.maps.Point(17, 34),
                  }}
                  onClick={() => handleMarkerClick(trip)}
                />
              );
            })}

            {Array.isArray(polylineData) &&
              polylineData.map((line, index) => {
                if (!Array.isArray(line.path) || line.path.length < 2)
                  return null;

                const start = line.path[0];
                const end = line.path[line.path.length - 1];

                const startLat = parseFloat(start?.lat);
                const startLng = parseFloat(start?.lng);
                const endLat = parseFloat(end?.lat);
                const endLng = parseFloat(end?.lng);

                const isValidCoord = (lat, lng) => !isNaN(lat) && !isNaN(lng);

                if (
                  !isValidCoord(startLat, startLng) ||
                  !isValidCoord(endLat, endLng)
                ) {
                  console.warn(
                    `Invalid start/end coordinates for polyline index ${index}`,
                    {
                      start: { lat: startLat, lng: startLng },
                      end: { lat: endLat, lng: endLng },
                    }
                  );
                  return null;
                }

                return (
                  <React.Fragment key={index}>
                    <Polyline
                      path={line.path.map((coord) => ({
                        lat: parseFloat(coord.lat),
                        lng: parseFloat(coord.lng),
                      }))}
                      options={{
                        strokeColor: line.color || "#000",
                        strokeOpacity: 0.9,
                        strokeWeight: 4,
                        geodesic: true,
                      }}
                    />

                    <OverlayView
                      position={{ lat: startLat, lng: startLng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div className="badge-overlay">{"Truck"}</div>
                    </OverlayView>

                    <Marker
                      position={{ lat: startLat, lng: startLng }}
                      icon={{
                        url: Flag,
                        scaledSize: new window.google.maps.Size(32, 32),
                        anchor: new window.google.maps.Point(16, 32),
                      }}
                    />
                    <OverlayView
                      position={{ lat: endLat, lng: endLng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div className="badge-overlay">{"Truck"}</div>
                    </OverlayView>

                    <Marker
                      position={{ lat: endLat, lng: endLng }}
                      icon={{
                        url: Flag,
                        scaledSize: new window.google.maps.Size(28, 28),
                        anchor: new window.google.maps.Point(14, 28),
                      }}
                    />
                  </React.Fragment>
                );
              })}

            {selectedTrip && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedTrip.lat),
                  lng: parseFloat(selectedTrip.lng),
                }}
                onCloseClick={() => setSelectedTrip(null)}
              >
                <div className="popup-content">
                  <div className="d-flex justify-content-between align-items-center mb-0">
                    <div className="d-flex gap-2">
                      <div className="popup-index-box">
                        {selectedTrip.id || ""}
                      </div>
                      <div className="popup-index-content">
                        <h5>{selectedTrip.truck_name}</h5>
                        <span>{selectedTrip.driver}</span>
                      </div>
                    </div>
                  </div>

                  <Nav tabs className="headerTabs_Map_Container">
                    {["State"].map((tab) => (
                      <NavItem key={tab}>
                        <NavLink
                          className={activeTab === tab ? "active-tab" : ""}
                          onClick={() => setActiveTab(tab)}
                        >
                          {tab}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  {activeTab === "State" && (
                    <div>
                      <div className="d-flex align-items-start gap-3 popup-state-section mt-1">
                        <img
                          src={Location}
                          alt="Location"
                          style={{ marginTop: "14px" }}
                        />
                        <div className="w-100">
                          <span>{selectedTrip.timestamp}</span>
                          <br />
                          <span>{selectedTrip.location}</span>
                          <br />
                          <span>{`On the move ${
                            selectedTrip.speed
                          } km/h — Odometer: ${
                            selectedTrip.odometer || "0"
                          }`}</span>
                        </div>
                      </div>

                      <div className="popup-icon-row d-flex align-items-start gap-2 mt-2">
                        <img src={RouteIconUrl} alt="Route" />
                        <div className="w-100">
                          <div className="d-flex justify-content-between">
                            <span>Driver commitment hours:</span>
                            <span>{selectedTrip.commitment_hours || "0"}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Driver driving hours:</span>
                            <span>
                              {selectedTrip.driving_hours} -{" "}
                              {selectedTrip.driving_km} Km
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Remaining guide:</span>
                            <span>to rest {selectedTrip.remaining_rest}</span>
                          </div>
                        </div>
                      </div>

                      <div className="popup-icon-row d-flex align-items-center gap-2 mt-2">
                        <img src={FuelIconUrl} alt="Fuel" />
                        <span>{stationName}</span>
                      </div>

                      <div className="popUp-icons-item d-flex align-items-center gap-3 mt-2">
                        <div className="d-flex align-items-center gap-3">
                          <img src={PlusIconUrl} alt="Plus" />
                          <span>DetailPathStreet</span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <img src={HumanIconUrl} alt="Human" />
                          <span>DetailPathStreet</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </CardBody>
    </Card>
  );
};
